import { useState } from 'react';
import type { OnChangeValue } from 'react-select';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';
import { makeCustomOption } from '@utils/makeCustomOption';
import { normalizeYoSearch } from '@utils/normalizeYoString';

import { makeValueFromSelected, resolvePlaceholder } from '../../utils/selectHelpers';
import { getMenuPortalProps } from '../../getMenuPortalProps';
import type { OptionType } from '../../Select';

interface Params {
    defaultOptions: OptionType[];
    value: OptionType[] | undefined;
    selectedValues: string[] | number[] | undefined;
    customValues: string[] | undefined;
    placeholder: string | undefined;
    title: string | undefined;
    forcePlaceholder: boolean | undefined;
    name: string | undefined;
    clearAfterSelect: boolean | undefined;
    menuPortalTarget: HTMLElement | null | undefined;
    loadOptions(inputValue: string): Promise<OptionType[]> | void;
    onChange(selectedItem: Nullable<OptionType[]>, name?: string): void;
    onCreateOption?(value: OptionType[], name?: string): void;
}

interface Data {
    value: OptionType[];
    placeholder: string | undefined;
    portalProps: ReturnType<typeof getMenuPortalProps>;
    inputValue: string;
    handleChange(selectedItem: OnChangeValue<OptionType, true>): void;
    handleCreateOption(label: string): void;
    handleInputChange(newValue: string): string;
    handleLoadOptions(inputValue: string): Promise<OptionType[]>;
    handleFocus(): void;
    handleBlur(): void;
}

export function useAsyncMultiSelect({
    defaultOptions,
    value,
    selectedValues,
    customValues,
    placeholder,
    title,
    forcePlaceholder,
    name,
    clearAfterSelect,
    menuPortalTarget,
    loadOptions,
    onChange,
    onCreateOption,
}: Params): Data {
    const { isOnFocus, handleFocus, handleBlur } = useCheckOnFocus();
    const [inputValue, setInputValue] = useState('');

    const baseValue =
        selectedValues && selectedValues.length > 0 ? makeValueFromSelected(defaultOptions, selectedValues) : value;
    const customOptions = customValues?.map(makeCustomOption) ?? [];

    return {
        value: [...(baseValue ?? []), ...customOptions],
        placeholder: resolvePlaceholder({ forcePlaceholder, placeholder, title, isOnFocus }),
        portalProps: getMenuPortalProps(menuPortalTarget, undefined, true),
        inputValue,
        handleChange: selectedItem => {
            onChange(selectedItem.length ? [...selectedItem] : null, name);
            if (clearAfterSelect) {
                setInputValue('');
            }
        },
        handleCreateOption: label => (onCreateOption ?? onChange)([makeCustomOption(label)], name),
        handleInputChange: newValue => {
            setInputValue(newValue);

            return newValue;
        },
        handleLoadOptions: query => normalizeYoSearch(query, loadOptions).then(items => items ?? []),
        handleFocus,
        handleBlur,
    };
}
